import React, { useCallback, useContext, useState } from "react";
import { ModalConfirm } from "oolib";
import { useQueryClient } from "react-query";
import { AppSettingsContext } from "../../../../contexts/appSettingsContext";
import { useDeleteContent } from "../../../../pageTpls/ContributeTpl/queryHooks/useDeleteContent";
import { __GetContentTypeConfigNew } from "../../../../utils/getters/gettersV2";
import { _Locale } from "../../../locale/Locale";

/**
 * The delete flow (confirm modal + mutation) lives here at table level —
 * a single instance — rather than inside every TableRowElem, so that each
 * row doesn't carry its own useDeleteContent subscription + modal closure.
 * Rows just flag which row wants deletion via requestRowDelete.
 *
 * Returns:
 * - requestRowDelete(rowData): stable callback for rows (row's
 *   'deleteContent' action calls this)
 * - deleteModal: rendered ModalConfirm when a delete is pending, else null.
 *   TableElem just drops it into its JSX.
 */
export const useTableRowDelete = () => {
  const { APP_SETTINGS } = useContext(AppSettingsContext);
  const queryClient = useQueryClient();
  const { mutate: deleteContentMutate } = useDeleteContent();
  const [rowPendingDelete, setRowPendingDelete] = useState(null);

  const requestRowDelete = useCallback(
    (rowData) => setRowPendingDelete(rowData),
    []
  );

  const handleDelete = (rowData) => {
    setRowPendingDelete(null);
    deleteContentMutate(
      {
        contentType: rowData.meta.kp_content_type,
        id: rowData.kp_og_published_doc || rowData._id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries("contentListing", [
            rowData.meta.kp_content_type,
          ]);
          queryClient.invalidateQueries("myContent");
        },
      }
    );
  };

  const deleteModal = rowPendingDelete ? (
    <ModalConfirm
      title={
        // if localizatio doesnt exist at all OR if it exists and lang === english then..
        // be more specific with this statment
        !APP_SETTINGS.lang || APP_SETTINGS.lang?.value === "en"
          ? `Are you sure you want to delete this ${
              __GetContentTypeConfigNew(rowPendingDelete.meta.kp_content_type)
                .general.content?.singular || "content"
            } ?`
          : _Locale(`Are you sure you want to delete this ? <word1: ${__GetContentTypeConfigNew(rowPendingDelete.meta.kp_content_type).general
            .content?.singular}>`)
      }
      onClose={() => setRowPendingDelete(null)}
      onConfirm={() => handleDelete(rowPendingDelete)}
    />
  ) : null;

  return { requestRowDelete, deleteModal };
};
